import React from "react";
import { Button, Checkbox } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import MathRenderer from "./MathRenderer";
import { v4 as uuidv4 } from "uuid";
import Slide from "@mui/material/Slide";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper component for explanation section
const ExplanationSection = ({ explanation }) => {
  if (!explanation) return null;

  return (
    <div className="mt-6 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
      <div className="flex items-center space-x-2 mb-3">
        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <svg
            className="w-3 h-3 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
        </div>
        <span className="text-sm font-semibold text-green-700">
          💡 Lời giải chi tiết
        </span>
      </div>
      <div className="text-sm text-green-800 prose prose-sm max-w-none">
        <MathRenderer content={explanation} />
      </div>
    </div>
  );
};

export default function ViewQuestion({ openDialog, setOpenDialog, q }) {
  const handleViewQuestion = () => {
    const {
      question,
      contentQuestions,
      type,
      imageUrl,
      videoUrl,
      explanation,
    } = q;
    switch (type) {
      case "TN":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="heading text-left text-[1.2rem] mb-2 question-label font-bold">
              {question}:
            </h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
              {videoUrl && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M8 5v10l8-5-8-5z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-purple-700">
                      Video hướng dẫn
                    </span>
                  </div>

                  {videoUrl.includes("youtube.com") ||
                  videoUrl.includes("youtu.be") ? (
                    <div className="aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={videoUrl
                          .replace("watch?v=", "embed/")
                          .replace("youtu.be/", "youtube.com/embed/")}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="rounded-lg"
                        title="Video hướng dẫn"
                      ></iframe>
                    </div>
                  ) : videoUrl.includes("vimeo.com") ? (
                    <div className="aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={videoUrl.replace(
                          "vimeo.com/",
                          "player.vimeo.com/video/"
                        )}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="rounded-lg"
                        title="Video hướng dẫn"
                      ></iframe>
                    </div>
                  ) : (
                    <div className="p-3 bg-white rounded border">
                      <p className="text-sm text-gray-600 mb-2">Link video:</p>
                      <a
                        href={videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm break-all underline"
                      >
                        {videoUrl}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-5 text-black">
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-500 text-white font-bold">
                  A
                </div>
                <span className="ml-3 manrope-font-size">
                  <MathRenderer content={q?.contentAnswerA} />
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500 text-white font-bold">
                  B
                </div>
                <span className="ml-3 manrope-font-size">
                  <MathRenderer content={q?.contentAnswerB} />
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-yellow-500 text-white font-bold">
                  C
                </div>
                <span className="ml-3 manrope-font-size">
                  <MathRenderer content={q?.contentAnswerC} />
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-500 text-white font-bold">
                  D
                </div>
                <span className="ml-3 manrope-font-size">
                  <MathRenderer content={q?.contentAnswerD} />
                </span>
              </div>

              <ExplanationSection explanation={explanation} />
            </div>
          </div>
        );
      case "DS":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <div className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </div>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>
            <div className="overflow-x-auto md:max-w-[100%] max-w-[400px] m-auto">
              <table className="table-contaiter styled-table">
                <thead>
                  <tr>
                    <th rowSpan="2" className="question">
                      Mệnh đề
                    </th>
                    <th colSpan="2" className="text-center">
                      Nội dung
                    </th>
                    <th rowSpan="2" className="correct">
                      Đúng
                    </th>
                    <th rowSpan="2" className="wrong ">
                      Sai
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {["a", "b", "c", "d"].map((option, index) => {
                    const content = [
                      q?.contentYA,
                      q?.contentYB,
                      q?.contentYC,
                      q?.contentYD,
                    ][index];
                    if (content) {
                      return (
                        <tr key={option}>
                          <td className="question">{option})</td>
                          <td
                            className="statement manrope-font-size"
                            colSpan="2"
                          >
                            <MathRenderer content={content} />
                          </td>
                          <td className="correct">
                            <Checkbox disabled />
                          </td>
                          <td className="wrong">
                            <Checkbox disabled />
                          </td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>

              <ExplanationSection explanation={explanation} />
            </div>
          </div>
        );
      case "KT":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="heading text-left text-[1.2rem] mb-2 question-label font-bold">
              {question}:
            </h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="w-[70%] m-auto"
                ></img>
              )}
            </div>
            <DragDropContext>
              <div className="container mx-auto p-6">
                <div key={question} className="mb-8">
                  <Droppable droppableId={question}>
                    {(provided) => (
                      <div
                        // ref={provided.innerRef}
                        // {...provided.droppableProps}
                        className="p-2 rounded mt-2 min-h-[50px] w-[100%] border-2 border-dashed border-teal-600"
                      >
                        <div className="flex flex-wrap lg:space-x-2">
                          {q?.items?.map((item, index) => (
                            <Draggable>
                              {(provided) => (
                                <div
                                  //   ref={provided.innerRef}
                                  //   {...provided.draggableProps}
                                  //   {...provided.dragHandleProps}
                                  className={
                                    "bg-blue-500 text-white p-2 min-w-16 rounded text-center mr-2 lg:mt-0 mt-2 manrope-font-size"
                                  }
                                >
                                  <MathRenderer content={item?.content} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                        </div>
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>

                  <div className="mt-4">
                    {q?.contentY1 && (
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex manrope-font-size">
                          <strong>1) </strong>&nbsp; &nbsp;
                          <MathRenderer content={q?.contentY1} />
                        </div>
                        <Droppable droppableId="slot1">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="p-2 rounded min-h-[50px] lg:w-[10%] w-[30%] border-2 border-dashed border-teal-600"
                            >
                              {q?.answers?.slot1 && (
                                <div className="bg-blue-500 text-white p-2 w-16 rounded text-center">
                                  <MathRenderer
                                    content={q?.answers?.slot1?.content}
                                  />
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                    {q?.contentY2 && (
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex manrope-font-size">
                          <strong>2) </strong>&nbsp; &nbsp;
                          <MathRenderer content={q?.contentY2} />{" "}
                        </div>

                        <Droppable droppableId="slot2">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="p-2 rounded min-h-[50px] lg:w-[10%] w-[30%] border-2 border-dashed border-teal-600"
                            >
                              {q?.answers?.slot2 && (
                                <div className="bg-blue-500 text-white p-2 w-16 rounded text-center">
                                  <MathRenderer
                                    content={q?.answers?.slot2?.content}
                                  />
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}

                    {q?.contentY3 && (
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex manrope-font-size">
                          <strong>3) </strong>&nbsp; &nbsp;
                          <MathRenderer content={q?.contentY3} />
                        </div>

                        <Droppable droppableId="slot3">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="p-2 rounded min-h-[50px] lg:w-[10%] w-[30%] border-2 border-dashed border-teal-600"
                            >
                              {q?.answers?.slot3 && (
                                <div className="bg-blue-500 text-white p-2 w-16 rounded text-center">
                                  <MathRenderer
                                    content={q?.answers?.slot3?.content}
                                  />
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}

                    {q?.contentY4 && (
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="flex manrope-font-size">
                          <strong>4) </strong>&nbsp; &nbsp;
                          <MathRenderer content={q?.contentY4} />
                        </div>
                        <Droppable droppableId="slot4">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="p-2 rounded min-h-[50px] lg:w-[10%] w-[30%] border-2 border-dashed border-teal-600"
                            >
                              {q?.answers?.slot4 && (
                                <div className="bg-blue-500 text-white p-2 w-16 rounded text-center">
                                  <MathRenderer
                                    content={q?.answers?.slot4?.content}
                                  />
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DragDropContext>

            <ExplanationSection explanation={explanation} />
          </div>
        );
      case "TLN":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="heading text-left text-[1.2rem] mb-2 question-label font-bold">
              {question}:
            </h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>

            <ExplanationSection explanation={explanation} />
          </div>
        );
      case "TLN_M":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="heading text-left text-[1.2rem] mb-2 question-label font-bold">
              {question}:
            </h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>
            {q?.contentY1 && (
              <div className="mt-4 mb-4">
                <div className="flex manrope-font-size">
                  <strong>1.</strong>&nbsp; &nbsp;
                  <MathRenderer content={q?.contentY1} />
                </div>
              </div>
            )}
            {q?.contentY2 && (
              <div className="mb-4">
                <div className="flex manrope-font-size">
                  <strong>2.</strong>&nbsp; &nbsp;
                  <MathRenderer content={q?.contentY2} />
                </div>
              </div>
            )}
            {q?.contentY3 && (
              <div className="mb-4">
                <div className="flex manrope-font-size">
                  <strong>3.</strong>&nbsp; &nbsp;
                  <MathRenderer content={q?.contentY3} />
                </div>
              </div>
            )}
            {q?.contentY4 && (
              <div className="">
                <div className="flex manrope-font-size">
                  <strong>4.</strong>&nbsp; &nbsp;
                  <MathRenderer content={q?.contentY4} />
                </div>
              </div>
            )}

            <ExplanationSection explanation={explanation} />
          </div>
        );
      case "MQ":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="text-[18px] font-bold italic mb-5">{q.title}</h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>

            <ExplanationSection explanation={explanation} />
          </div>
        );
      case "MA":
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="text-[18px] font-bold italic mb-5">{q.title}</h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>
            <div>
              {q?.contentC1 && (
                <div className="flex items-center">
                  <Checkbox
                    disabled
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                  />{" "}
                  <MathRenderer content={q?.contentC1} />
                </div>
              )}
              {q?.contentC2 && (
                <div className="flex items-center">
                  <Checkbox
                    disabled
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                  />{" "}
                  <MathRenderer content={q?.contentC2} />
                </div>
              )}
              {q?.contentC3 && (
                <div className="flex items-center">
                  <Checkbox
                    disabled
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                  />{" "}
                  <MathRenderer content={q?.contentC3} />
                </div>
              )}
              {q?.contentC4 && (
                <div className="flex items-center">
                  <Checkbox
                    disabled
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                  />{" "}
                  <MathRenderer content={q?.contentC4} />
                </div>
              )}
            </div>

            <ExplanationSection explanation={explanation} />
          </div>
        );
      default:
        return (
          <div
            id={question}
            className="mx-auto relative mt-4 page p-[30px] bg-gray-300 hover:bg-gray-200 rounded-[24px] opacity-100 shadow-lg text-black"
          >
            <h2 className="heading text-left text-[1.2rem] mb-2 question-label font-bold">
              {question}:
            </h2>
            <span className="question-content text-[18px]">
              <MathRenderer content={contentQuestions} />
            </span>
            <div>
              {" "}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={`pimath-${uuidv4()}`}
                  className="lg:w-[35%] md:w-[40%] sm:w-[60%] w-[70%] m-auto"
                ></img>
              )}
            </div>

            <ExplanationSection explanation={explanation} />
          </div>
        );
    }
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => setOpenDialog(false)}
      aria-describedby="alert-dialog-slide-description"
      maxWidth={"md"}
    >
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description">
          {handleViewQuestion()}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        <Button onClick={() => setOpenDialog(false)}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
}
